import { useFetcher } from '@remix-run/react'
import { useEffect, useState } from 'react'
import {
  ValidatedForm,
  useControlField,
  useFormContext,
} from 'remix-validated-form'
import {
  aCookieContact,
  aListingContact,
  aUser,
  useAtom,
  useAtomValue,
  useSetAtom,
} from '~/atoms'
import { LoadingCircle, TextField } from '~/components'
import { Button, Dialog, Textarea, toast } from '~/components/ui'
import {
  ActivityContact,
  CookieContact,
  Listing,
  brokerContactValidator,
} from '~/models'

function BrokerContactForm({
  listing,
  setIsHidden,
}: {
  listing: Listing
  setIsHidden: (hidden: boolean) => void
}) {
  const user = useAtomValue(aUser)
  const setListingContact = useSetAtom(aListingContact)
  const [cookieContact, setCookieContact] = useAtom(aCookieContact)
  const fetcher = useFetcher<{
    success: boolean
    fileUrl?: string
    listingContact: ActivityContact
    cookieContact: CookieContact
  }>()
  const formId = 'broker-contact-form'
  const formContext = useFormContext(formId)
  const [value, setValue] = useControlField<string>('message', formId)

  useEffect(() => {
    if (fetcher.state === 'idle' && fetcher.data) {
      if (fetcher.data.success === true) {
        if (fetcher.data.listingContact) {
          setListingContact(fetcher.data.listingContact)
        }
        if (fetcher.data.cookieContact) {
          setCookieContact(fetcher.data.cookieContact)
        }
        toast({
          variant: 'success',
          title: 'Broker Message Sent',
          description:
            'Thank you for your submission. A broker will be in contact with you shortly.',
        })
        setIsHidden(true)
      } else {
        toast({
          variant: 'destructive',
          title: 'Problem Sending Message',
          description:
            'There was a problem sending your message. Please try again later or contact support.',
        })
      }
    }
  }, [fetcher])

  return (
    <>
      <ValidatedForm
        id={formId}
        validator={brokerContactValidator}
        method="post"
        action="/api/actions/contact-broker"
        fetcher={fetcher}
        defaultValues={user ?? cookieContact ?? undefined}>
        <div className="grid grid-cols-12 gap-6">
          <input type="hidden" name="_id" value={listing._id} />
          <TextField
            name="firstName"
            placeholder="First Name*"
            className="col-span-6"
            disabled={fetcher.state === 'submitting'}
          />
          <TextField
            name="lastName"
            placeholder="Last Name*"
            className="col-span-6"
            disabled={fetcher.state === 'submitting'}
          />
          <TextField
            name="email"
            placeholder="Email*"
            type="email"
            className="col-span-6"
            disabled={fetcher.state === 'submitting'}
          />
          <TextField
            name="phone"
            placeholder="Phone"
            type="tel"
            className="col-span-6"
            disabled={fetcher.state === 'submitting'}
          />
          <Textarea
            name="message"
            placeholder="Message"
            defaultValue={`Please send me more information about your listing: ${listing.name}`}
            className="col-span-12"
            disabled={fetcher.state === 'submitting'}
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        </div>
      </ValidatedForm>
      <div className="mt-6 flex justify-end">
        <Button
          type="submit"
          disabled={fetcher.state !== 'idle'}
          onClick={async (e) => {
            e.preventDefault()
            formContext.submit()
          }}>
          {fetcher.state !== 'idle' && (
            <LoadingCircle className="mr-2 h-5 w-5" />
          )}
          {fetcher.state !== 'idle' ? 'Sending' : 'Send'}
        </Button>
      </div>
    </>
  )
}

export default function BrokerContact({ listing }: { listing: Listing }) {
  const [isOpen, setIsOpen] = useState(false)
  const [isHidden, setIsHidden] = useState(false)

  const title =
    listing.brokers.length === 1 ? 'Contact Broker' : 'Contact Brokers'

  if (isHidden) {
    return null
  }

  return (
    <>
      <div className="block md:hidden">
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <Dialog.Trigger asChild>
            <Button type="button">{title}</Button>
          </Dialog.Trigger>
          <Dialog.Content className="sm:max-w-[425px]">
            <Dialog.Header>
              <Dialog.Title className="mb-3 font-normal">{title}</Dialog.Title>
              <Dialog.Description className="sr-only">
                {title}
              </Dialog.Description>
            </Dialog.Header>
            <BrokerContactForm listing={listing} setIsHidden={setIsHidden} />
          </Dialog.Content>
        </Dialog>
      </div>

      {!isOpen && (
        <div className="hidden rounded-md border p-4 md:block">
          <h4 className="mb-4 text-lg font-medium">{title}</h4>
          <BrokerContactForm listing={listing} setIsHidden={setIsHidden} />
        </div>
      )}
    </>
  )
}
